/**
 * Il condotto largo: come sale un file che non entra in una richiesta HTTP.
 *
 * PERCHE' ESISTE, E PERCHE' ERA SPENTO
 *
 * Cloud Run accetta ~32MB di corpo per richiesta. Chi manda base64 dentro un
 * JSON ne perde un altro quarto, perche' il base64 gonfia di 4/3: tetto reale
 * ~24MB. Una lezione registrata di un'ora sta ben oltre.
 *
 * La strada alternativa era gia' scritta e non la chiamava nessuno: la
 * `extractTextViaGCS` dentro backgroundFileProcessor.js aveva zero chiamanti, e
 * l'unico punto che avrebbe dovuto usarla diceva
 * `// GCS fallback temporarily disabled for testing` e rilanciava l'errore.
 * Qui quella strada diventa una sola, usata da tutti i percorsi di caricamento
 * e per ogni tipo di file, non solo per i PDF.
 *
 * COME FUNZIONA
 *
 * Il file sale su Firebase Storage con l'SDK, quindi autenticato e dentro il
 * percorso che le regole di Storage concedono al proprietario e a nessun altro.
 * Poi si chiede al server di estrarre il testo passando SOLO il nome del blob:
 * il server decide da se' il bucket e verifica che il blob stia nel ramo di chi
 * chiede. Il file temporaneo viene cancellato in ogni caso, anche quando
 * l'estrazione fallisce.
 */

import { ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { auth, storage } from '@redux/firebase';

// Stesso schema di textExtractor.js e backgroundFileProcessor.js: l'indirizzo
// del server si ricava dall'ambiente del build. Non c'e' un modulo unico che lo
// esporti, e non lo introduco qui: sarebbe un cambio che tocca file che altre
// sessioni stanno modificando.
let API_URL;
if (process.env.REACT_APP_ENVIRONMENT === 'development') {
  API_URL = 'http://' + process.env.REACT_APP_API_URL_DEVELOPMENT;
} else if (process.env.REACT_APP_ENVIRONMENT === 'preproduction') {
  API_URL = 'https://' + process.env.REACT_APP_API_URL_PREPRODUCTION;
} else {
  API_URL = 'https://' + process.env.REACT_APP_API_URL_PRODUCTION;
}
const API_BASE_URL = API_URL ? API_URL.replace(/["']/g, '') : 'http://localhost:8080';

/**
 * Da qui in su si passa dal condotto largo.
 *
 * 20 MiB e non 32: il tetto di Cloud Run vale sul corpo INTERO della richiesta,
 * e alcune strade vecchie ci mettono dentro base64 (che gonfia di 4/3) piu' il
 * resto del JSON. 32 / (4/3) fa 24 MiB esatti, e il margine che resta serve a
 * non finire nella fascia in cui il browser manda e il server risponde 413.
 * Sotto questa soglia le strade di prima vanno benissimo e costano meno: niente
 * caricamento intermedio.
 */
export const SOGLIA_CONDOTTO_LARGO = 20 * 1024 * 1024;

/**
 * Il ramo temporaneo su Storage.
 *
 * Il nome parla di PDF ma qui ci passa di tutto: audio, video, docx, pptx. E'
 * rimasto perche' e' l'unico prefisso che le regole di Storage VIVE permettono
 * al client di scrivere:
 *
 *     match /temp-pdf-extraction/{uid}/{file=**} {
 *       allow read, write: if isOwner(uid);
 *     }
 *
 * Rinominarlo vorrebbe dire deployare regole nuove, cioe' un cambio separato
 * con un rischio suo. Meglio un nome impreciso che una regola messa di fretta.
 */
const PREFISSO_TEMP = 'temp-pdf-extraction';

export function passaDalCondottoLargo(file) {
  return Boolean(file) && file.size >= SOGLIA_CONDOTTO_LARGO;
}

async function tokenDiAccesso() {
  const utente = auth?.currentUser;
  if (!utente) {
    throw new Error('Sessione scaduta: rientra e riprova');
  }
  return utente.getIdToken();
}

/**
 * Carica il file su Storage ed estrae il testo lato server.
 *
 * @param {File} file
 * @param {Object} opzioni
 * @param {string} opzioni.documentId
 * @param {string} opzioni.userId
 * @param {Function} [opzioni.onProgress] riceve { fase, progress, message }
 * @returns {Promise<{text: string, parziale: boolean, metadata: Object}>}
 */
export async function estraiViaStorage(file, { documentId, userId, onProgress } = {}) {
  // Chi chiama non sempre ha gia' un documentId: il caricamento diretto crea il
  // documento DOPO aver estratto il testo. In quel caso basta un nome univoco,
  // tanto il file vive pochi secondi. L'utente invece deve essere quello vero,
  // perche' il server confronta il ramo del blob con l'uid del token: un uid
  // preso da altrove farebbe fallire la richiesta con un 403 incomprensibile.
  const proprietario = userId || auth?.currentUser?.uid;
  if (!proprietario) {
    throw new Error('Sessione scaduta: rientra e riprova');
  }
  const cartella = documentId || `diretto-${Date.now()}`;

  const nomeBlob = `${PREFISSO_TEMP}/${proprietario}/${cartella}/${file.name}`;
  const riferimento = ref(storage, nomeBlob);
  const avvisa = (fase, progress, message) => {
    if (typeof onProgress === 'function') onProgress({ fase, progress, message });
  };

  try {
    // Ripartibile, non in un colpo solo: su rete mobile un caricamento da
    // centinaia di MB in una botta sola si spezza e ricomincia da zero, e il
    // mobile e' il 35% del traffico. In piu' da' l'avanzamento VERO, che e'
    // l'unica barra onesta che possiamo mostrare durante il caricamento.
    await new Promise((risolvi, rifiuta) => {
      const compito = uploadBytesResumable(riferimento, file, {
        contentType: file.type || 'application/octet-stream',
        customMetadata: { originalName: file.name, documentId: cartella },
      });
      compito.on(
        'state_changed',
        (stato) => {
          const percentuale = stato.totalBytes
            ? Math.round((stato.bytesTransferred / stato.totalBytes) * 100)
            : 0;
          avvisa('upload', percentuale, `Caricamento del file: ${percentuale}%`);
        },
        rifiuta,
        risolvi
      );
    });

    avvisa('extracting', 0, 'Lettura del contenuto in corso...');

    const risposta = await fetch(`${API_BASE_URL}/api/extract-from-gcs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await tokenDiAccesso()}`,
      },
      body: JSON.stringify({
        blob_name: nomeBlob,
        filename: file.name,
        mime_type: file.type || '',
      }),
    });

    if (!risposta.ok) {
      const dettaglio = await risposta.json().catch(() => ({}));
      throw new Error(
        dettaglio.detail || `Estrazione non riuscita (${risposta.status})`
      );
    }

    const esito = await risposta.json();
    const metadata = esito.metadata || {};

    return {
      text: esito.text,
      parziale: Boolean(metadata.parziale),
      metadata,
    };
  } finally {
    // Anche quando e' andata male: il file resta nel ramo dell'utente e occupa
    // spazio che paghiamo noi. Il server prova a cancellarlo per conto suo, ma
    // se la richiesta non lo ha nemmeno raggiunto quella pulizia non parte.
    try {
      await deleteObject(riferimento);
    } catch (erroreP) {
      // Il file non c'e' piu' (l'ha tolto il server) oppure la rete e' caduta:
      // in nessuno dei due casi vale la pena disturbare chi sta caricando.
      if (erroreP?.code !== 'storage/object-not-found') {
        console.warn('Pulizia del file temporaneo non riuscita:', erroreP?.code);
      }
    }
  }
}
